// labData.js
// Data puppeteer for the /lab Screen Lab. Every screen in this app is a
// stateless render of Firestore data, so the Lab tests screens by seeding
// real docs in the exact shapes each state needs — the real listeners,
// transactions, and rules all run. No mock render paths exist anywhere.
//
// Everything written here carries `_isTestData: true` (same regime as
// utils/testData.js) so the public builders skip it (see CLAUDE.md
// "Test-data rule") and cleanTestData() purges it.
//
// HARD BLOCK (load-bearing): openTestShift() refuses while any non-test
// shift is active. There is exactly ONE "active shift" globally — overlays,
// /events live badges, and the register all key off it — so a test shift
// opened at a live market would hijack live surfaces. The check runs at
// tap-time, immediately before the write.

import { db } from "../firebase";
import {
  collection, getDocs, getDoc, query, where, runTransaction,
  addDoc, setDoc, updateDoc, doc, Timestamp, serverTimestamp,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { seedTestBooking } from "./testData";
import { COMBOS, COMBO_ORDER, comboAddOnsFor } from "./combos";

const TEST_FLAG = "_isTestData";

// Default stage durations for synthesized timelines (time-warp baselines).
// Chosen to look realistic on overlay timers + customer wait estimates.
const DRAW_MS = 4 * 60 * 1000;
const COOK_MS = 3 * 60 * 1000;

// ── ACTIVE SHIFT STATUS ─────────────────────────────────────────────────────
export async function getActiveShiftStatus() {
  const snap = await getDocs(query(collection(db, "shifts"), where("isActive", "==", true)));
  const shifts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return {
    real: shifts.filter(s => !s[TEST_FLAG]),
    test: shifts.filter(s => !!s[TEST_FLAG]),
  };
}

// ── TEST SHIFT LIFECYCLE ────────────────────────────────────────────────────
// Mirrors ShiftManager.openShift()'s doc shape (subset: every field a
// consumer reads) + the meta counter + the config/liveShift broadcast.
// Always links to a TEST booking — test bookings never enter
// config/publicEvents, so the public live badge can't light up.
export async function openTestShift() {
  const { real, test } = await getActiveShiftStatus();
  if (real.length > 0) {
    const name = real[0].eventName || real[0].id;
    throw new Error(
      `BLOCKED — a real shift ("${name}") is active. The Lab never opens a test shift while a real shift is live.`
    );
  }
  if (test.length > 0) return { id: test[0].id, existing: true };

  let bookingId = null;
  const bkSnap = await getDocs(query(collection(db, "bookings"), where(TEST_FLAG, "==", true)));
  if (!bkSnap.empty) bookingId = bkSnap.docs[0].id;
  if (!bookingId) bookingId = (await seedTestBooking()).id;

  // Inherit the Event Config preview stamp (config/stream.previewFlash*) so a
  // Lab test shift mirrors the staged event's flash boards. A live shift beats
  // `previewWhenIdle` everywhere, so hard-coding the base set here flipped
  // every overlay/order surface off the staged per-event designs the moment a
  // test shift opened (2026-06-12). Unreadable/absent stamp → base set.
  let flashProfileIds = [];
  let includeBaseFlashSet = true;
  let comboKeys = [...COMBO_ORDER]; // default: all combos on, so drills work out of the box
  let roadPricing = null;
  try {
    const streamSnap = await getDoc(doc(db, "config", "stream"));
    const sd = streamSnap.exists() ? streamSnap.data() : {};
    if (Array.isArray(sd.previewFlashProfileIds)) flashProfileIds = sd.previewFlashProfileIds;
    if (typeof sd.previewIncludeBaseFlashSet === "boolean") includeBaseFlashSet = sd.previewIncludeBaseFlashSet;
    if (Array.isArray(sd.previewComboKeys)) comboKeys = sd.previewComboKeys;
  } catch (err) {
    console.warn("openTestShift: preview stamp read failed, using base set", err?.message);
  }
  // Road Mode drill: a test shift inherits the STAGED Event Config setup
  // (even an already-consumed staging) — pricing AND the marquee/showcase
  // suppressions — so the sandbox run shows exactly what tomorrow's real
  // shift will show. Suppressed Gazette cards rendering in the drill but not
  // the event confused the pre-Berwyn check (2026-08-28).
  let disabledMarqueeTexts = [];
  let disabledShowcaseIds = [];
  try {
    const setupSnap = await getDoc(doc(db, "config", "eventSetup"));
    const sdc = setupSnap.exists() ? setupSnap.data()?.shiftDefaults : null;
    if (sdc?.pricing && typeof sdc.pricing === "object") roadPricing = sdc.pricing;
    if (Array.isArray(sdc?.disabledMarqueeTexts)) disabledMarqueeTexts = sdc.disabledMarqueeTexts;
    if (Array.isArray(sdc?.disabledShowcaseIds)) disabledShowcaseIds = sdc.disabledShowcaseIds;
  } catch (err) {
    console.warn("openTestShift: eventSetup read failed", err?.message);
  }

  const shiftDoc = {
    openedAt: serverTimestamp(),
    closedAt: null,
    queueClosedAt: null,
    isActive: true,
    isQueueOpen: true,
    livestreamJoinEnabled: true,
    contextMode: "both",
    staffEmail: "jump@thejoyofpancakes.com",
    eventName: "🧪 Lab Test Shift",
    venueAddress: "Screen Lab",
    venueIsPublic: false,
    artistRoster: ["Dan"],
    bookingId,
    activeFlashProfileIds: flashProfileIds,
    includeBaseFlashSet,
    activeComboKeys: comboKeys,
    pricing: roadPricing,
    disabledMarqueeTexts,
    disabledShowcaseIds,
    ordersCompleted: 0, ordersRejected: 0, ordersExpired: 0,
    flashCount: 0, customCount: 0, photosCaptured: 0,
    finishedWithoutPhotoCount: 0,
    peakQueueDepth: 0, busiestHour: null, forceClose: false,
    currentNumber: 0, lastNumberIssued: 0,
    [TEST_FLAG]: true,
  };
  const ref = await addDoc(collection(db, "shifts"), shiftDoc);
  await addDoc(collection(db, "shifts", ref.id, "meta"), { id: "counter", lastNumber: 0 });

  setDoc(doc(db, "config", "liveShift"), {
    bookingId,
    shiftId: ref.id,
    eventName: shiftDoc.eventName,
    isLive: true,
    openedAt: serverTimestamp(),
    contextMode: "both",
  }).catch(err => console.warn("lab liveShift write failed:", err?.message));

  return { id: ref.id, existing: false };
}

export async function closeTestShift(shiftId) {
  await updateDoc(doc(db, "shifts", shiftId), {
    isActive: false,
    isQueueOpen: false,
    closedAt: serverTimestamp(),
    queueClosedAt: serverTimestamp(),
  });
  setDoc(doc(db, "config", "liveShift"), {
    bookingId: null, shiftId: null, eventName: null,
    isLive: false, openedAt: null,
  }).catch(err => console.warn("lab liveShift clear failed:", err?.message));
}

// Gate toggles — isQueueOpen ("paused"), livestreamJoinEnabled, contextMode.
export async function setShiftGates(shiftId, fields) {
  const allowed = {};
  if ("isQueueOpen" in fields) {
    allowed.isQueueOpen = !!fields.isQueueOpen;
    allowed.queueClosedAt = fields.isQueueOpen ? null : serverTimestamp();
  }
  if ("livestreamJoinEnabled" in fields) allowed.livestreamJoinEnabled = !!fields.livestreamJoinEnabled;
  if ("contextMode" in fields) allowed.contextMode = fields.contextMode;
  await updateDoc(doc(db, "shifts", shiftId), allowed);
}

// ── STOCK ART ───────────────────────────────────────────────────────────────
// Flash design art (transparent PNGs already in Storage) doubles as puppet
// pancake photos — photo_url, photo_cutout_url, and thumbnail stand-ins.
// No camera, no Replicate cost, keepsake state in one tap.
export async function getStockArt() {
  const snap = await getDoc(doc(db, "config", "flashDesigns"));
  if (!snap.exists()) return [];
  const designs = Array.isArray(snap.data().designs) ? snap.data().designs : [];
  return designs
    .filter(d => d.imageUrl)
    .map(d => ({ id: d.id, label: d.label || d.id, url: d.imageUrl }));
}

// ── PUPPET ORDERS ───────────────────────────────────────────────────────────
const PUPPET_NAMES = ["Ada", "Basil", "Clem", "Dot", "Ezra", "Fern", "Gus", "Hazel", "Iris", "Jude"];
const PUPPET_SUBJECTS = ["a lighthouse", "her dog Biscuit", "a hot air balloon", "his banjo", "a sea otter", "a sunflower"];
const PUPPET_COLORS = [["orange", "pink"], ["blue", "yellow"], ["violet", "green"], ["red", "gold"]];

async function nextQueueNumber(shiftId) {
  const counterSnap = await getDocs(collection(db, "shifts", shiftId, "meta"));
  if (counterSnap.empty) throw new Error("Shift counter missing — open the test shift via the Lab.");
  const counterRef = counterSnap.docs[0].ref;
  return runTransaction(db, async (tx) => {
    const counterDoc = await tx.get(counterRef);
    const next = (counterDoc.data().lastNumber ?? 0) + 1;
    tx.update(counterRef, { lastNumber: next });
    return next;
  });
}

// Spawns one puppet order (token + order, queued) into the test shift.
// opts: { orderType: "custom"|"flash"|"magic", designId, designLabel,
//         recipientName, subjectName, notes, favoriteColors, imageUrl }
export async function spawnPuppetOrder(shiftId, opts = {}) {
  const i = Math.floor(Math.random() * PUPPET_NAMES.length);
  const orderType = opts.orderType || "custom";
  const queueNumber = await nextQueueNumber(shiftId);

  const tokenId = crypto.randomUUID();
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    createdAt: serverTimestamp(),
    expiresAt: null,
    usedAt: serverTimestamp(),
    status: "used",
    ticketType: orderType === "flash" ? "flash" : orderType === "magic" ? "magic" : "custom",
    source: "test_harness",
    issuedBy: "screen_lab",
    [TEST_FLAG]: true,
  });

  const orderRef = await addDoc(collection(db, "orders"), {
    tokenId,
    shiftId,
    orderType,
    state: "queued",
    queueNumber,
    recipientName: opts.recipientName || PUPPET_NAMES[i],
    subjectName: opts.subjectName ?? (orderType === "custom" ? PUPPET_SUBJECTS[i % PUPPET_SUBJECTS.length] : null),
    notes: opts.notes || "",
    favoriteColors: opts.favoriteColors || PUPPET_COLORS[i % PUPPET_COLORS.length],
    designId: opts.designId || null,
    designLabel: opts.designLabel || null,
    // Combo drill: exact real-flow field shapes (comboKey + addOns[]) so the
    // queue badges, delivery checkpoint, and close guard are all drillable.
    ...(opts.comboKey && COMBOS[opts.comboKey]
      ? { comboKey: opts.comboKey, addOns: comboAddOnsFor(opts.comboKey), comboStampedBy: "lab" }
      : {}),
    submittedAt: serverTimestamp(),
    originalQueuePosition: queueNumber,
    suspensionCount: 0,
    portalEnabled: true,
    portal_state: "active",
    nft_status: "none",
    // Reference image for custom orders so the artist view + Reference
    // overlay have something to render (flash orders resolve design art).
    imageUrl: opts.imageUrl || null,
    croppedImageUrl: opts.imageUrl || null,
    photo_url: null, photo_cutout_url: null, customer_email: null,
    email_marketing_opt_in: false, ipfs_url: null,
    event_name: null, event_location: null, keepsake_expires_at: null,
    startedAt: null, flippedAt: null, completedAt: null,
    rejectedAt: null, suspendedAt: null, resubmittedAt: null,
    drawingTimeMs: null, cookTimeMs: null,
    [TEST_FLAG]: true,
  });

  // Mirror RegisterView/ShiftManager bookkeeping the overlays read.
  updateDoc(doc(db, "shifts", shiftId), { lastNumberIssued: queueNumber }).catch(() => {});

  return { id: orderRef.id, tokenId, queueNumber, url: `${window.location.origin}/order?t=${tokenId}` };
}

// Batch spawn: { custom: 3, flash: 2, magic: 0 } → 5 puppets. Flash puppets
// rotate through the provided stockArt designs so they resolve real art.
export async function spawnPuppetBatch(shiftId, composition, stockArt = []) {
  const created = [];
  const plan = [];
  for (const [type, n] of Object.entries(composition)) {
    for (let k = 0; k < (n || 0); k++) plan.push(type);
  }
  let f = 0;
  for (const type of plan) {
    const opts = { orderType: type };
    if (type === "flash" && stockArt.length > 0) {
      const art = stockArt[f++ % stockArt.length];
      opts.designId = art.id;
      opts.designLabel = art.label;
    }
    created.push(await spawnPuppetOrder(shiftId, opts));
  }
  return created;
}

// ── PUPPET STATE SCRUBBER ───────────────────────────────────────────────────
// Jumps a puppet order to any pipeline state, writing the SAME fields the
// real taps write (startOrder/markCooking/markFlipping in ArtistQueueView,
// photo capture in CameraView, markDelivered). Time-warp: `warpMinutes`
// backdates "time in current state" so timers + wait estimates look real.
//
// Reference field shapes:
//   drawing  → state, startedAt, artistName (+ shift.currentNumber)
//   cooking  → state, cookingStartedAt, drawingTimeMs
//   flipping → state, flippedAt
//   ready    → state, photo_url, photo_cutout_url, photographedAt,
//              readyToDeliverAt, cookTimeMs
//   done     → state, deliveredAt
export async function setPuppetState(order, target, { warpMinutes = 0, art = null } = {}) {
  const now = Date.now();
  const warpMs = Math.max(0, warpMinutes) * 60 * 1000;
  const ts = (ms) => Timestamp.fromMillis(ms);
  const u = { state: target };

  const artist = order.artistName || "Dan";
  const photo = art?.url || order.photo_url || null;

  switch (target) {
    case "queued":
      Object.assign(u, {
        startedAt: null, cookingStartedAt: null, flippedAt: null,
        photographedAt: null, readyToDeliverAt: null, deliveredAt: null,
        completedAt: null, drawingTimeMs: null, cookTimeMs: null,
        photo_url: null, photo_cutout_url: null, cutoutThumbnailUrl: null,
        suspendedAt: null, rejectedAt: null,
      });
      break;
    case "drawing":
      Object.assign(u, { startedAt: ts(now - warpMs), artistName: artist });
      break;
    case "cooking":
      Object.assign(u, {
        startedAt: ts(now - warpMs - DRAW_MS),
        artistName: artist,
        drawingTimeMs: DRAW_MS,
        cookingStartedAt: ts(now - warpMs),
      });
      break;
    case "flipping":
      // flippedAt = now so the flip countdown / "HERE WE GO!" primer plays
      // live on the artist view + cooking overlay.
      Object.assign(u, {
        startedAt: ts(now - warpMs - DRAW_MS - COOK_MS),
        artistName: artist,
        drawingTimeMs: DRAW_MS,
        cookingStartedAt: ts(now - warpMs - COOK_MS),
        flippedAt: ts(now),
      });
      break;
    case "ready_to_deliver":
      Object.assign(u, {
        startedAt: ts(now - warpMs - DRAW_MS - COOK_MS),
        artistName: artist,
        drawingTimeMs: DRAW_MS,
        cookingStartedAt: ts(now - warpMs - COOK_MS),
        flippedAt: ts(now - warpMs - Math.floor(COOK_MS / 2)),
        photographedAt: ts(now - warpMs),
        readyToDeliverAt: ts(now - warpMs),
        cookTimeMs: COOK_MS,
        photo_url: photo,
        photo_cutout_url: photo,
        cutoutThumbnailUrl: photo,
      });
      break;
    case "done":
      Object.assign(u, {
        startedAt: order.startedAt || ts(now - warpMs - DRAW_MS - COOK_MS),
        artistName: artist,
        drawingTimeMs: order.drawingTimeMs ?? DRAW_MS,
        cookingStartedAt: order.cookingStartedAt || ts(now - warpMs - COOK_MS),
        flippedAt: order.flippedAt || ts(now - warpMs - Math.floor(COOK_MS / 2)),
        photographedAt: order.photographedAt || ts(now - warpMs),
        readyToDeliverAt: order.readyToDeliverAt || ts(now - warpMs),
        cookTimeMs: order.cookTimeMs ?? COOK_MS,
        photo_url: order.photo_url || photo,
        photo_cutout_url: order.photo_cutout_url || photo,
        cutoutThumbnailUrl: order.cutoutThumbnailUrl || photo,
        deliveredAt: ts(now - warpMs),
      });
      break;
    case "suspended":
      Object.assign(u, {
        suspendedAt: serverTimestamp(),
        suspensionCount: (order.suspensionCount ?? 0) + 1,
      });
      break;
    case "rejected":
      Object.assign(u, { rejectedAt: serverTimestamp() });
      break;
    default:
      throw new Error(`Unknown puppet state: ${target}`);
  }

  await updateDoc(doc(db, "orders", order.id), u);

  // Mirror the shift bookkeeping the real taps do.
  if (order.shiftId) {
    if (target === "drawing" && order.queueNumber) {
      updateDoc(doc(db, "shifts", order.shiftId), { currentNumber: order.queueNumber }).catch(() => {});
    }
    if (target === "flipping" || target === "done") {
      updateDoc(doc(db, "shifts", order.shiftId), { lastActivityAt: serverTimestamp() }).catch(() => {});
    }
  }
}

// ── STANDBY PUPPET + SIMULATED PAYMENT (paid-path drill) ────────────────────
// Mirrors the order shape `livestreamJoin` writes for the standby lane
// (queue: "standby", state "queued", NO queueNumber until promoted). The
// 💳 Simulate Payment button then calls the devSimulatePayment callable,
// which runs the SAME promote transaction as verifyLivestreamPayment —
// the drill validates the production paid path with zero money.
export async function spawnStandbyPuppet(shiftId, opts = {}) {
  const i = Math.floor(Math.random() * PUPPET_NAMES.length);
  const name = opts.recipientName || PUPPET_NAMES[i];

  const tokenId = `live_test_${Date.now()}_${crypto.randomUUID().slice(0, 8)}`;
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    status: "used",
    usedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    ticketType: "flash",
    source: "test_harness",
    shiftId,
    [TEST_FLAG]: true,
  });

  const orderRef = await addDoc(collection(db, "orders"), {
    tokenId,
    shiftId,
    state: "queued",
    queue: "standby",
    recipientName: name,
    customerName: name,
    orderType: opts.orderType || "flash",
    designId: opts.designId || null,
    designLabel: opts.designLabel || opts.designId || null,
    isFreestyle: false,
    // Combo-intent drill: pendingComboKey is what createLivestreamCheckout
    // stamps at link mint; devSimulatePayment's promote then converts it to
    // comboKey + addOns — the exact production grant path.
    ...(opts.pendingComboKey && COMBOS[opts.pendingComboKey] ? { pendingComboKey: opts.pendingComboKey } : {}),
    source: "test_harness",
    createdAt: serverTimestamp(),
    favoriteColors: opts.favoriteColors || PUPPET_COLORS[i % PUPPET_COLORS.length],
    photo_url: null, photo_cutout_url: null, customer_email: null,
    startedAt: null, flippedAt: null, completedAt: null,
    drawingTimeMs: null, cookTimeMs: null,
    [TEST_FLAG]: true,
  });
  await updateDoc(doc(db, "tokens", tokenId), { orderId: orderRef.id });

  return { id: orderRef.id, tokenId, url: `${window.location.origin}/order?t=${tokenId}` };
}

export async function simulatePayment(orderId) {
  const fn = httpsCallable(getFunctions(getApp()), "devSimulatePayment");
  const res = await fn({ orderId });
  return res.data; // { promoted, queueNumber } | { already, queueNumber }
}

// ── TOKEN STATE FIXTURES ────────────────────────────────────────────────────
// Tokens crafted into hard-to-reach customer states.
export async function createExpiredToken(ticketType = "custom") {
  const tokenId = crypto.randomUUID();
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(Date.now() - 60 * 60 * 1000), // expired 1h ago
    usedAt: null,
    status: "issued",
    ticketType,
    source: "test_harness",
    issuedBy: "screen_lab",
    [TEST_FLAG]: true,
  });
  return { tokenId, url: `${window.location.origin}/order?t=${tokenId}` };
}

export function invalidTokenUrl() {
  return `${window.location.origin}/order?t=lab-invalid-token-${Math.random().toString(36).slice(2, 8)}`;
}

// ── BASKET PUPPET (Phase 3 compound-order manifest drills) ──────────────────
// Spawns an _isTestData basket (N open slots) + its master basket token, and
// returns the manifest URL. The manifest screen is a stateless render of the
// basket doc, so the drills are: open it twice and race a slot (double-serve
// — the submitBasketSlot transaction picks one winner), or kill the tab and
// reopen (resumability — nothing client-side to lose).
export async function spawnBasketPuppet(shiftId, combo = { flash: 3, custom: 1 }, label = "Lab Party Pack") {
  const slots = [
    ...Array.from({ length: combo.flash || 0 }, () => ({ ticketType: "flash", status: "open", orderId: null })),
    ...Array.from({ length: combo.custom || 0 }, () => ({ ticketType: "custom", status: "open", orderId: null })),
  ].map((s, idx) => ({ idx, ...s }));
  const basketRef = await addDoc(collection(db, "baskets"), {
    shiftId,
    label,
    source: "lab",          // future: "register" (party button) | "combo" (store bundle SKU)
    comboKey: null,         // future: combo products stamp their SKU key here
    paymentRef: "lab",
    slots,
    createdAt: serverTimestamp(),
    [TEST_FLAG]: true,
  });
  const tokenId = `bskm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    status: "used",
    ticketType: "basket",
    source: "lab",
    basketId: basketRef.id, // routes /order?t= to the manifest
    shiftId,
    createdAt: serverTimestamp(),
    usedAt: serverTimestamp(),
    [TEST_FLAG]: true,
  });
  return { basketId: basketRef.id, tokenId, url: `${window.location.origin}/order?t=${tokenId}` };
}

// ── CAMPAIGN LANDING FIXTURE ────────────────────────────────────────────────
// Creates an _isTestData campaign + one promo token in the exact
// printCampaignTokens() shape, so /order?t= routes to the real
// CampaignLanding. Scan tracking increments hit only the test docs;
// cleanTestData() sweeps both (campaigns is in its collection list).
export async function createCampaignFixture() {
  const campRef = await addDoc(collection(db, "campaigns"), {
    name:             "🧪 Lab test campaign",
    archived:         false,
    ticketTagline:    "You got handed a Golden Ticket.",
    ticketExpiryText: "",
    serialPrefix:     "LAB",
    headline:         "You found a golden ticket! 🥞",
    body:             "This is the Lab's test campaign landing — exactly what a guest sees scanning a campaign QR in the wild.",
    heroImageUrl:     null,
    buttons:          [{ label: "See the pancakes", url: "/gallery" }],
    linkedOrderId:    null,
    expirationMode:   "never",
    expirationMinutes: 1440,
    expirationDate:   null,
    delivery:         "print",
    recipients:       [],
    landingMode:      "discovery",
    totalPrinted:     1,
    totalScanned:     0,
    lastScannedAt:    null,
    printBatches:     [],
    createdAt:        serverTimestamp(),
    [TEST_FLAG]:      true,
  });
  const tokenId = `cmp_lab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(doc(db, "tokens", tokenId), {
    id: tokenId,
    status: "used",
    ticketType: "promo",
    source: "campaign",
    campaignId: campRef.id,
    orderId: null,
    batchLabel: "lab-fixture",
    catalogNumber: null,
    snapshotTagline: "You got handed a Golden Ticket.",
    snapshotExpiryText: null,
    snapshotSerialPrefix: "LAB",
    expiresAt: null,
    scannedAt: null,
    scanCount: 0,
    lastScannedAt: null,
    createdAt: serverTimestamp(),
    [TEST_FLAG]: true,
  });
  return { tokenId, url: `${window.location.origin}/order?t=${tokenId}` };
}
